import * as React from "react";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { ErrorRouteComponent } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { useAppTheme } from "@/hooks/useAppTheme";

export function DefaultErrorComponent({
  error,
}: React.ComponentProps<ErrorRouteComponent>) {
  const { t } = useTranslation();
  const router = useRouter();
  const isLight = useAppTheme() === "steward";
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  React.useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  return (
    <div className="container mx-auto flex min-h-[calc(100svh-var(--bottom-nav-height)-var(--top-nav-height))] flex-col items-center justify-center gap-5 px-[var(--horizontal-gutter)] py-[var(--vertical-gutter)]">
      <h2 className="text-2xl font-medium">{t("error.title")}</h2>
      <p className="text-muted-foreground text-center text-lg text-balance">
        {t("error.description")}
      </p>
      <div>
        <Button
          variant="secondary"
          size="default"
          onClick={async () => {
            await router.invalidate();
          }}
        >
          {t("error.buttonLabel")}
        </Button>
      </div>
      <p className="text-muted-foreground text-center text-lg text-balance">
        {t("error.ifNothingWorks")}{" "}
        <a
          href="#"
          className={
            isLight
              ? // Explicit hex (== steward tokens): this error UI mounts from the
                // root router and may render outside any .steward-theme scope,
                // where var(--green)/--ground/--ink would be undefined.
                "text-[#2f8f4e] underline"
              : "text-blue-500 underline"
          }
          onClick={() => window.location.reload()}
        >
          {t("error.reloadPage")}
        </a>
      </p>
      {import.meta.env.DEV && (
        <div className="flex w-full flex-col gap-2">
          <pre
            style={{
              fontSize: ".7em",
              border: "1px solid red",
              borderRadius: ".25rem",
              padding: ".3rem",
              color: "red",
              overflow: "auto",
            }}
          >
            {error.message ? <code>{error.message}</code> : null}
          </pre>
          <details
            className={
              isLight
                ? "rounded-xl border border-[#e2e2dd] bg-[#fafaf8] p-2 font-mono text-base text-[#0b0b0c]"
                : "rounded-xl bg-black p-2 font-mono text-base text-gray-100"
            }
          >
            {error.stack}
          </details>
        </div>
      )}
    </div>
  );
}
